import {
  MediaType,
  type STMap,
  type STMapMarker,
  type STMapMarkerImage,
  type STMapMarkerPoint,
  type STMapMarkerPulse,
  type STMapMarkerScene,
  type STMapMarkerText,
  STMapMarkerType,
} from '@types';
import getImageSize from '@utils/getImageSize';
import { hexToColorString } from '@utils/hexToColorString';
import { getSysUrl, getUrlByMediaType } from '@utils/urlMakers';
import type { CSSProperties } from 'react';
import {
  IMG_RADAR,
  MARKER_IMAGE_SIZE,
  MARKER_SIZE,
  RADAR_SIZE,
} from '../constants';

type STAMapInitOptions = {
  target: string | HTMLDivElement;
  initialView?: { lng: number; lat: number; zoom: number };
  mapStyle?: STMap['layerStyle'];
  onMarkerClick: (id: string) => void;
  onMarkerMoveEnd: (params: { id: string; lng: number; lat: number }) => void;
  onRadarRotate: (angle: number) => void;
  onLoaded?: () => void;
};

const BASE_ZINDEX = 100;
const SELELCTED_ZINDEX = 9999;

export type AMapObjectType =
  | AMap.Marker
  | AMap.Text
  | AMap.Polyline
  | AMap.Circle
  | AMap.Polygon;

export class STAMap {
  private map?: AMap.Map | null;

  private loca?: typeof Loca | null;

  private markerObjectMap: Map<string, AMapObjectType> = new Map();

  private zoomWatchingMap: Map<string, AMap.Marker | AMap.Text> = new Map();

  private animationMarkerMap: Map<string, AMap.Marker> = new Map();

  private layerMap: Map<
    string,
    { layer: typeof Loca.Layer; data: STMapMarkerPulse }
  > = new Map();

  private zIndexMap: Map<string, number> = new Map();

  private zIndex: number = BASE_ZINDEX;

  private onMarkerClick?: STAMapInitOptions['onMarkerClick'];

  private onMarkerMoveEnd?: STAMapInitOptions['onMarkerMoveEnd'];

  private onRadarRotate?: STAMapInitOptions['onRadarRotate'];

  private editingId?: string;

  private radar?: AMap.Marker;

  private isRadarOn = false;

  private isRotating = false;

  private layers: AMap.TileLayer[] = [];

  static editingZIndex = SELELCTED_ZINDEX;

  init({
    target,
    initialView,
    mapStyle = 'normal',
    onMarkerClick,
    onMarkerMoveEnd,
    onRadarRotate,
    onLoaded,
  }: STAMapInitOptions) {
    this.onMarkerClick = onMarkerClick;
    this.onMarkerMoveEnd = onMarkerMoveEnd;
    this.onRadarRotate = onRadarRotate;
    if (!this.map) {
      this.map = new AMap.Map(target, {});
    }
    if (!this.loca) {
      this.loca = new Loca.Container({
        map: this.map,
      });
    }
    this.loca.animate.start();
    this.map.clearMap();

    this.markerObjectMap = new Map();
    this.zoomWatchingMap = new Map();
    this.animationMarkerMap = new Map();
    this.layerMap = new Map();
    this.zIndexMap = new Map();

    if (initialView) {
      this.map.setZoomAndCenter(
        initialView.zoom,
        [initialView.lng, initialView.lat],
        true
      );
    }
    this.setMapStyle(mapStyle);

    this.radar = new AMap.Marker({
      content: this.generateRadarIcon(),
      cursor: 'ew-resize',
      anchor: 'center',
      zIndex: SELELCTED_ZINDEX - 1,
    });

    this.radar.on('mousedown', () => {
      this.isRotating = true;
      this.map?.setStatus({ dragEnable: false });
      document.addEventListener('mouseup', this.onMouseUp);
      // @ts-ignore
      document.addEventListener('mousemove', this.onMouseMove);
    });
    this.map?.on('zoomchange', () => {
      const items = Array.from(this.zoomWatchingMap.values());
      items.forEach((item) => {
        const itemData = STAMap.getExtData(item).markerData;
        const itemType = itemData.type;
        if (itemType === STMapMarkerType.image) {
          this.generateImageContent(itemData.icon).then((iconObj) => {
            (item as AMap.Marker).setIcon(iconObj);
          });
        } else if (itemType === STMapMarkerType.text) {
          if (itemData.icon.stick) {
            (item as AMap.Text).setStyle(this.generateTextStyle(itemData));
          }
        }
      });
    });
    this.map?.on('complete', () => {
      onLoaded?.();
    });
  }

  private generateRadarIcon = (scale = 1) => {
    const width = RADAR_SIZE * scale;
    const height = RADAR_SIZE * scale;
    return `<img style="width:${width}px;height:${height}px;" src="${IMG_RADAR}" draggable="false" />`;
  };

  /**
   * 生成 Marker 图标的 HTML 字符串
   */
  generateMarkerIcon = (
    data: STMapMarkerPoint['icon'],
    opt: { isSelected?: boolean; isActive?: boolean } = {}
  ) => {
    const {
      type = 0,
      scaleX,
      scaleY,
      borderColor,
      fillColor,
      activeBorderColor,
      activeFillColor,
      mediaIcon,
    } = data;
    if (type === 1 && mediaIcon) {
      const width = MARKER_IMAGE_SIZE * scaleX;
      const height = MARKER_IMAGE_SIZE * scaleY;
      const url = getUrlByMediaType(
        mediaIcon.type ? MediaType.lib : MediaType.system,
        mediaIcon.url
      );
      const markerString = `<img style="width:${width}px;height:${height}px;" src="${url}" draggable="false" />`;
      if (opt.isSelected) {
        // const posMap = {
        //   'top-left': 'top:-6px;left:-6px',
        //   'top-center': 'top:-6px',
        //   'top-right': 'top:-6px;right:-6px',
        //   'middle-left': 'left:-6px',
        //   center: '',
        //   'middle-right': 'right:-6px',
        //   'bottom-left': 'bottom:-6px;left:-6px',
        //   'bottom-center': 'bottom:-6px',
        //   'bottom-right': 'bottom:-6px;right:-6px',
        // };
        // const pos = posMap[mediaIcon.anchor];
        // const anchorString = `<img style="position:absolute;width:12px;height:12px;${pos}" src="${IMG_ANCHOR}" draggable={false} />`;
        return `<div style="display:flex;justify-content:center;align-items:center;position:relative;outline:2px #F36264 solid;">${markerString}</div>`;
      }
      return markerString;
    }

    const width = MARKER_SIZE * scaleX;
    const height = MARKER_SIZE * scaleY;
    const bgc = opt.isActive ? activeFillColor : fillColor;
    const bc = opt.isActive ? activeBorderColor : borderColor;
    const markerString = `<div style="width:${width}px;height:${height}px;background-color:rgba(${bgc});border:2px solid rgba(${bc});border-radius:9999px;"/>`;
    if (opt.isSelected) {
      return `<div style="border:2px #F36264 solid;padding:8px;border-radius:3px;background-color:rgba(0,0,0,.1);">${markerString}</div>`;
    }
    return markerString;
  };

  /**
   * 生成 Marker 的 Label 字符串
   */
  generateMarkerLabel = (
    data: STMapMarkerScene | STMapMarkerPoint,
    opt: { isSelected?: boolean } = {}
  ) => {
    const { edge, ox, oy, align, font, background, writingMode } =
      data.titleSetting;
    const {
      bgColor = '0,0,0,0.5',
      borderWidth = 0,
      borderColor = '0,0,0,0.5',
      bgRoundedge = 5,
      type: bgType = 0,
      gradientAngle = 0,
      gradientStartColor = '0,0,0,0.5',
      gradientEndColor = '0,0,0,0.5',
    } = background;
    const { fontColor, fontSize } = font;

    let { title } = data;
    if (opt.isSelected && !title) {
      title = '请输入标记标题';
    }

    let content = '';

    if (data.showTitle && !!title) {
      const isVertical = writingMode === 1;
      const bgStr =
        bgType === 1
          ? `linear-gradient(${gradientAngle}deg, rgba(${gradientStartColor}), rgba(${gradientEndColor}))`
          : `rgba(${bgColor})`;
      const stylesArr: string[] = [
        'white-space:pre',
        `background:${bgStr}`,
        `padding:${isVertical ? '10px 5px' : '5px 10px'}`,
        `border-radius:${bgRoundedge}px`,
        `ling-height:${isVertical ? 1.6 : 1.4}`,
        `font-size:${fontSize}px`,
        `color:rgba(${fontColor})`,
        `text-align:${align}`,
        `border:${borderWidth}px solid rgba(${borderColor})`,
        'user-select: none',
      ];
      if (isVertical) {
        stylesArr.push('letter-spacing:4px');
        stylesArr.push('writing-mode:vertical-lr');
      }

      content = `<div style="${stylesArr.join(';')}">${title}</div>`;
    }

    return {
      content,
      direction: edge,
      offset: [ox, oy],
    };
  };

  /**
   * 生成 Text 的样式
   */
  generateTextStyle = (
    data: STMapMarkerText,
    opt: { isSelected?: boolean } = {}
  ): CSSProperties => {
    const { icon, titleSetting } = data;
    const { zoom, stick } = icon;
    const { align, font, background, writingMode } = titleSetting;
    const { isSelected } = opt;
    const {
      bgColor = '0,0,0,0.5',
      borderWidth = 0,
      borderColor = '0,0,0,0.5',
      bgRoundedge = 5,
      type: bgType = 0,
      gradientAngle = 0,
      gradientStartColor = '0,0,0,0.5',
      gradientEndColor = '0,0,0,0.5',
    } = background;
    const { fontColor, fontSize } = font;

    const isVertical = writingMode === 1;
    const bgStr =
      bgType === 1
        ? `linear-gradient(${gradientAngle}deg, rgba(${gradientStartColor}), rgba(${gradientEndColor}))`
        : `rgba(${bgColor})`;
    const styles: CSSProperties = {
      whiteSpace: 'pre',
      background: bgStr,
      padding: isVertical ? '10px 5px' : '5px 10px',
      borderRadius: `${bgRoundedge}px`,
      lineHeight: isVertical ? 1.6 : 1.4,
      fontSize: `${fontSize}px`,
      color: `rgba(${fontColor})`,
      textAlign: align,
      border: `${borderWidth}px solid rgba(${borderColor})`,
      userSelect: 'none',
    };
    if (isVertical) {
      styles.letterSpacing = '4px';
      styles.writingMode = 'vertical-lr';
    }
    if (isSelected) {
      styles.outline = '2px #F36264 solid';
    }
    if (stick) {
      const curZoom = this.map?.getZoom() ?? 13;
      const scale = this.getZoomScale({ zoom: curZoom, baseZoom: zoom });
      styles.transform = `scale(${scale})`;
    }

    return styles;
  };

  /** 贴图 */
  generateImageContent = async (
    icon: STMapMarkerImage['icon'],
    opt: { isSelected?: boolean } = {}
  ) => {
    const { scaleX, image, zoom } = icon;
    const curZoom = this.map?.getZoom() ?? 13;
    const zoomScale = this.getZoomScale({ zoom: curZoom, baseZoom: zoom });
    const scale = scaleX * zoomScale;

    if (!icon.image.url) {
      const size = 100 * scale;
      return new AMap.Icon({
        size: new AMap.Size(size, size),
        image: getSysUrl(
          '/imgs/imgplaceholder.aa70f314b7a2823e5085bebda62d231c.png'
        ),
        imageSize: new AMap.Size(size, size),
      });
    }
    const src = getUrlByMediaType(
      image.type === 0 ? MediaType.system : MediaType.lib,
      image.url
    );
    const size = await getImageSize(src);
    if (!size) throw new Error('获取图片尺寸失败');
    const { width: imgWidth, height: imgHeight } = size;
    const width = imgWidth * scale;
    const height = imgHeight * scale;
    return new AMap.Icon({
      size: new AMap.Size(width, height),
      image: src,
      imageSize: new AMap.Size(width, height),
    });
  };

  getZoomScale({
    zoom,
    baseZoom = 13,
  }: { zoom: number; baseZoom?: number }): number {
    const defaultZoom = baseZoom;
    const defaultSize = 1;
    const diff = zoom - defaultZoom;
    // 根据差值计算大小百分比
    // 使用**运算符来计算放大或缩小的倍数
    // 例如，diff为1时，表示放大1倍，即2 ** 1 = 2
    // diff为-1时，表示缩小一倍，即2 ** -1 = 0.5
    return defaultSize * 2 ** diff;
  }

  static getLngLat = (pos?: AMap.LngLat | null) => {
    const lng = pos?.getLng() ?? 0;
    const lat = pos?.getLat() ?? 0;
    return { lng, lat };
  };

  static getExtData = (object: AMapObjectType) => {
    return object.getExtData() as { markerData: STMapMarker };
  };

  /**
   * 获取标记的 z-index 值。
   */
  private getZIndex = (id: string) => {
    const zIndex = this.zIndexMap.get(id);
    if (zIndex !== undefined) {
      return zIndex;
    }
    this.zIndex += 1;
    this.zIndexMap.set(id, this.zIndex);
    return this.zIndex;
  };

  private onMouseMove = (e: { pixel: AMap.Pixel }) => {
    if (!e.pixel) return;
    const pos = this.radar?.getPosition();
    if (!pos) return;
    const xy = this.map?.lngLatToContainer(pos);
    if (!xy) return;
    const angle =
      Math.atan2(e.pixel.x - xy.x, xy.y - e.pixel.y) * (180 / Math.PI);
    this.radar?.setAngle(angle);
    this.onRadarRotate?.(angle);
  };

  private onMouseUp = () => {
    document.removeEventListener('mouseup', this.onMouseUp);
    // @ts-ignore
    document.removeEventListener('mousemove', this.onMouseMove);
    this.map?.setStatus({ dragEnable: true });
    this.isRotating = false;
  };

  changeLayer = (layerType: STMap['layerType']) => {
    if (layerType === 0) {
      this.layers.forEach((layer) => {
        this.map?.removeLayer(layer);
      });
      this.layers = [];
    } else if (layerType === 1) {
      const layer = new AMap.TileLayer.Satellite();
      this.map?.addLayer(layer);
      this.layers.push(layer);
    }
  };

  reOrder = ({ markers }: { markers: STMapMarker[] }) => {
    this.zIndex = BASE_ZINDEX;
    this.zIndexMap.clear();
    markers.forEach((x) => {
      this.addMarker(x);
    });
  };

  getCenter = () => {
    const { lat, lng } = STAMap.getLngLat(this.map?.getCenter());
    const zoom = this.map?.getZoom() ?? 13;
    return { lat, lng, zoom };
  };

  setCenter = ({
    lng,
    lat,
    zoom,
  }: {
    lng: number;
    lat: number;
    zoom?: number;
  }) => {
    if (zoom) {
      this.map?.setZoomAndCenter(zoom, [lng, lat], true);
    } else {
      this.map?.setCenter([lng, lat], true);
    }
  };

  autoFit = () => {
    const list = Array.from(this.markerObjectMap?.values());
    const layerList = Array.from(this.layerMap.values()).map((x) => {
      return new AMap.Polyline({ path: x.data.icon.path });
    });
    this.map?.setFitView([...list, ...layerList], true);
  };

  removeMap = () => {
    Array.from(this.animationMarkerMap.values()).forEach((x) => {
      x.stopMove();
      x.setMap(null);
    });

    this.markerObjectMap.clear();
    this.animationMarkerMap.clear();
    this.layerMap.clear();
    this.zoomWatchingMap.clear();
    this.zIndexMap.clear();

    this.loca?.destroy();
    this.loca = undefined;
    this.map
      // @ts-ignore
      ?.getContext('webgl')
      ?.getExtension('WEBGL_lose_context')
      ?.loseContext();
    this.map?.destroy();
    this.map = undefined;
  };

  addMarker = (
    markerData: STMapMarker,
    opts: {
      isEditing?: boolean;
    } = {}
  ) => {
    // 如果已经存在则删除
    if (
      this.markerObjectMap.get(markerData.id) ||
      this.layerMap.get(markerData.id)
    ) {
      this.removeMarker(markerData.id);
    }

    const zIndex = this.getZIndex(markerData.id);
    const { isEditing = false } = opts;

    switch (markerData.type) {
      case undefined:
      case STMapMarkerType.scene:
      case STMapMarkerType.point: {
        const { id, showTitle, icon } = markerData;
        const marker = new AMap.Marker({
          position: [icon.lng, icon.lat],
          draggable: true,
          anchor:
            icon.type === 1
              ? icon.mediaIcon?.anchor ?? 'bottom-center'
              : 'center',
          zIndex,
          content: this.generateMarkerIcon(icon),
          ...(!!showTitle && { label: this.generateMarkerLabel(markerData) }),
          extData: { markerData },
          visible: false,
        });
        marker.on('click', () => {
          this.onMarkerClick?.(id);
        });
        marker.on('dragstart', () => {
          if (id === this.editingId) {
            this.hideRadar();
          }
        });
        marker.on('dragend', () => {
          const { lng, lat } = STAMap.getLngLat(marker.getPosition());
          this.onMarkerMoveEnd?.({ id, lng, lat });
          if (this.isRadarOn && id === this.editingId) {
            this.showRadar({ lng, lat });
          }
        });
        marker.on('mouseup', this.onMouseUp);
        this.markerObjectMap.set(id, marker);
        marker.setMap(this.map);
        marker.show();
        break;
      }
      case STMapMarkerType.text: {
        const { id, icon, title } = markerData;
        const marker = new AMap.Text({
          position: [icon.lng, icon.lat],
          draggable: true,
          anchor: 'center',
          text: title || '请输入标记标题',
          zIndex,
          visible: false,
          extData: { markerData },
          style: this.generateTextStyle(markerData),
        });
        marker.on('click', () => {
          this.onMarkerClick?.(id);
        });
        marker.on('dragend', () => {
          const { lng, lat } = STAMap.getLngLat(marker.getPosition());
          this.onMarkerMoveEnd?.({ id, lng, lat });
        });
        this.markerObjectMap.set(id, marker);
        this.zoomWatchingMap.set(id, marker);
        marker.setMap(this.map);
        marker.show();
        break;
      }
      case STMapMarkerType.image: {
        const { id, icon } = markerData;
        const marker = new AMap.Marker({
          position: [icon.lng, icon.lat],
          draggable: true,
          anchor: 'center',
          zIndex,
          extData: { markerData },
          visible: false,
        });
        marker.on('click', () => {
          this.onMarkerClick?.(id);
        });
        marker.on('dragend', () => {
          const { lng, lat } = STAMap.getLngLat(marker.getPosition());
          this.onMarkerMoveEnd?.({ id, lng, lat });
        });
        this.markerObjectMap.set(id, marker);
        this.zoomWatchingMap.set(id, marker);
        marker.setMap(this.map);
        this.generateImageContent(icon).then((iconObj) => {
          marker.setIcon(iconObj);
          marker.show();
        });
        break;
      }
      case STMapMarkerType.polyline: {
        const { id, icon } = markerData;
        const { path, strokeStyle, borderStyle, animation } = icon;
        if (!path.length) return;

        const polyline = new AMap.Polyline({
          path: path,
          cursor: 'pointer',
          isOutline: true,
          outlineColor: borderStyle.color,
          borderWeight: borderStyle.width,
          strokeColor: strokeStyle.color,
          strokeOpacity: strokeStyle.opacity,
          strokeWeight: strokeStyle.width,
          strokeStyle: strokeStyle.type,
          zIndex,
          extData: { markerData },
        });
        polyline.on('click', () => {
          this.onMarkerClick?.(id);
        });
        polyline.setMap(this.getMap());
        this.markerObjectMap.set(id, polyline);

        if (animation.type === 1) {
          const {
            markerIcon,
            markerWidth,
            markerHeight,
            markerScale = 1,
          } = animation;
          const width = markerWidth * markerScale;
          const height = markerHeight * markerScale;
          const animationMarker = new AMap.Marker({
            map: this.getMap(),
            position: path[0],
            icon: new AMap.Icon({
              size: new AMap.Size(width, height),
              image: getSysUrl(markerIcon),
              imageSize: new AMap.Size(width, height),
            }),
            anchor: 'center',
          });
          animationMarker.moveAlong(path, {
            duration: 500,
            autoRotation: true,
            circlable: true,
          });
          this.animationMarkerMap.set(id, animationMarker);
        }
        break;
      }
      case STMapMarkerType.pulse: {
        const { id, icon } = markerData;
        const { path, pulseStyle, animation } = icon;
        if (!path.length) return;

        const layer = new Loca.PulseLineLayer({
          zIndex: zIndex,
          opacity: 1,
          visible: true,
        });
        const source = new Loca.GeoJSONSource({
          data: {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'LineString',
                  coordinates: path,
                },
              },
            ],
          },
        });
        layer.setStyle({
          altitude: 0,
          lineWidth: pulseStyle.width,
          headColor: `rgba(${hexToColorString({
            hex: pulseStyle.headColor,
            alpha: pulseStyle.headOpacity,
          })})`,
          trailColor: `rgba(${hexToColorString({
            hex: pulseStyle.tailColor,
            alpha: pulseStyle.tailOpacity,
          })})`,
          interval: 0.25,
          duration: 5000,
        });
        layer.setSource(source);
        this.loca.add(layer);
        this.layerMap.set(id, { layer, data: markerData });

        if (animation.type === 1) {
          const {
            markerIcon,
            markerWidth,
            markerHeight,
            markerScale = 1,
          } = animation;
          const width = markerWidth * markerScale;
          const height = markerHeight * markerScale;
          const animationMarker = new AMap.Marker({
            map: this.getMap(),
            position: path[0],
            icon: new AMap.Icon({
              size: new AMap.Size(width, height),
              image: getSysUrl(markerIcon),
              imageSize: new AMap.Size(width, height),
            }),
            anchor: 'center',
          });
          animationMarker.moveAlong(path, {
            duration: 500,
            autoRotation: true,
            circlable: true,
          });
          this.animationMarkerMap.set(id, animationMarker);
        }
        break;
      }
      case STMapMarkerType.circle: {
        const { id, icon } = markerData;
        const {
          radius,
          lat,
          lng,
          fillStyle,
          borderStyle,
          blink,
          borderStyleActive,
          fillStyleActive,
        } = icon;
        if (!radius) return;
        const circle = new AMap.Circle({
          center: [lng, lat],
          cursor: 'pointer',
          radius,
          fillColor: fillStyle.color,
          fillOpacity: fillStyle.opacity,
          strokeColor: borderStyle.color,
          strokeWeight: borderStyle.width,
          strokeOpacity: borderStyle.opacity,
          zIndex,
          extData: { markerData },
        });
        circle.on('click', () => {
          this.onMarkerClick?.(id);
        });
        if (!isEditing) {
          let isMouseover = false;
          const toggleActive = (isActive: boolean) => {
            const isOn = isMouseover || isActive;
            circle.setOptions({
              fillColor: isOn ? fillStyleActive.color : fillStyle.color,
              fillOpacity: isOn ? fillStyleActive.opacity : fillStyle.opacity,
              strokeColor: isOn ? borderStyleActive.color : borderStyle.color,
              strokeWeight: isOn ? borderStyleActive.width : borderStyle.width,
              strokeOpacity: isOn
                ? borderStyleActive.opacity
                : borderStyle.opacity,
            });
          };
          circle.on('mouseover', () => {
            isMouseover = true;
            toggleActive(true);
          });
          circle.on('mouseout', () => {
            isMouseover = false;
            toggleActive(false);
          });
          if (blink) {
            let isActive = false;
            const intervalId = setInterval(() => {
              const obj = this.getMarkerObjectById(id);
              if (obj !== circle) {
                clearInterval(intervalId);
              } else {
                toggleActive(isActive);
                isActive = !isActive;
              }
            }, 1000);
          }
        }
        if (this.map) {
          circle.setMap(this.map);
        }
        this.markerObjectMap.set(id, circle);
        break;
      }
      case STMapMarkerType.polygon: {
        const { id, icon } = markerData;
        const {
          path,
          fillStyle,
          borderStyle,
          blink,
          borderStyleActive,
          fillStyleActive,
        } = icon;
        if (!path.length) return;
        // @ts-ignore
        const polygon = new AMap.Polygon({
          path: path,
          cursor: 'pointer',
          fillColor: fillStyle.color,
          fillOpacity: fillStyle.opacity,
          strokeColor: borderStyle.color,
          strokeWeight: borderStyle.width,
          strokeOpacity: borderStyle.opacity,
          zIndex,
          extData: { markerData },
        });
        polygon.on('click', () => {
          this.onMarkerClick?.(id);
        });
        if (!isEditing) {
          let isMouseover = false;
          const toggleActive = (isActive: boolean) => {
            const isOn = isMouseover || isActive;
            polygon.setOptions({
              fillColor: isOn ? fillStyleActive.color : fillStyle.color,
              fillOpacity: isOn ? fillStyleActive.opacity : fillStyle.opacity,
              strokeColor: isOn ? borderStyleActive.color : borderStyle.color,
              strokeWeight: isOn ? borderStyleActive.width : borderStyle.width,
              strokeOpacity: isOn
                ? borderStyleActive.opacity
                : borderStyle.opacity,
            });
          };
          polygon.on('mouseover', () => {
            isMouseover = true;
            toggleActive(true);
          });
          polygon.on('mouseout', () => {
            isMouseover = false;
            toggleActive(false);
          });
          if (blink) {
            let isActive = false;
            const intervalId = setInterval(() => {
              const obj = this.getMarkerObjectById(id);
              if (obj !== polygon) {
                clearInterval(intervalId);
              } else {
                toggleActive(isActive);
                isActive = !isActive;
              }
            }, 1000);
          }
        }
        if (this.map) {
          polygon.setMap(this.map);
        }
        this.markerObjectMap.set(id, polygon);
        if (icon.animation.type === 1) {
          const {
            markerIcon,
            markerWidth,
            markerHeight,
            markerScale = 1,
          } = icon.animation;
          const width = markerWidth * markerScale;
          const height = markerHeight * markerScale;
          const animationMarker = new AMap.Marker({
            map: this.getMap(),
            position: path[0],
            icon: new AMap.Icon({
              size: new AMap.Size(width, height),
              image: getSysUrl(markerIcon),
              imageSize: new AMap.Size(width, height),
            }),
            anchor: 'center',
          });
          animationMarker.moveAlong([...path, path[0]], {
            duration: 500,
            autoRotation: true,
            circlable: true,
          });
          this.animationMarkerMap.set(id, animationMarker);
        }
        break;
      }
      default:
        break;
    }
  };

  removeMarker = (id: string) => {
    const marker = this.markerObjectMap.get(id);
    if (marker) {
      marker.setMap(null);
      this.markerObjectMap.delete(id);
    }

    this.zoomWatchingMap.delete(id);

    const animationMarker = this.animationMarkerMap.get(id);
    if (animationMarker) {
      animationMarker.stopMove();
      animationMarker.setMap(null);
      this.animationMarkerMap.delete(id);
    }

    const layer = this.layerMap.get(id)?.layer;
    if (layer) {
      layer.destroy();
      this.layerMap.delete(id);
    }
  };

  removeLayer = (id: string) => {
    const layer = this.layerMap.get(id)?.layer;
    if (layer) {
      layer.destroy();
      this.layerMap.delete(id);
    }
  };

  /** 开关是否显示雷达光 */
  setRadarFlag = (isOn: boolean) => {
    this.isRadarOn = isOn;
  };

  hideRadar = () => {
    this.radar?.setMap(null);
  };

  showRadar({ lng, lat }: { lng: number; lat: number }) {
    this.radar?.setPosition([lng, lat]);
    this.radar?.setMap(this.map);
  }

  updateRadarSize = (scale: number) => {
    if (this.radar) {
      this.radar.setContent(this.generateRadarIcon(scale));
    }
  };

  setEditingId = (id: string | undefined) => {
    this.editingId = id;
  };

  updateAngle = (angle: number) => {
    if (!this.isRotating) {
      this.radar?.setAngle(angle);
    }
  };

  setMapStyle = (mapStyle: string) => {
    this.map?.setMapStyle(`amap://styles/${mapStyle}`);
  };

  getMarkerObjectById = (id: string) => {
    const markerObject = this.markerObjectMap.get(id);
    if (!markerObject) return null;
    return markerObject;
  };

  getAnimationMarkerById = (id: string) => {
    const markerObject = this.animationMarkerMap.get(id);
    if (!markerObject) return null;
    return markerObject;
  };

  getLayerById = (id: string) => {
    const layer = this.layerMap.get(id);
    if (!layer) return null;
    return layer;
  };

  centerMarker = (markerData: STMapMarker) => {
    switch (markerData.type) {
      case undefined:
      case STMapMarkerType.scene:
      case STMapMarkerType.point:
      case STMapMarkerType.circle:
      case STMapMarkerType.polyline:
      case STMapMarkerType.polygon: {
        const markerObject = this.getMarkerObjectById(markerData.id);
        if (markerObject) {
          this.getMap().setFitView([markerObject], true);
        }
        break;
      }
      case STMapMarkerType.text:
      case STMapMarkerType.image: {
        this.getMap().setZoomAndCenter(
          markerData.icon.zoom,
          [markerData.icon.lng, markerData.icon.lat],
          true
        );
        break;
      }
      case STMapMarkerType.pulse: {
        let polyline: AMap.Polyline | undefined = new AMap.Polyline({
          path: markerData.icon.path,
        });
        this.getMap().setFitView([polyline], true);
        polyline = undefined;
        break;
      }
    }
  };

  getMap = () => {
    if (!this.map) throw new Error('地图未加载完成');
    return this.map;
  };

  calculateCenterPoint = (
    coordinates: Array<[number, number]>
  ): [number, number] => {
    let totalLng = 0;
    let totalLat = 0;

    for (const [lng, lat] of coordinates) {
      totalLng += lng;
      totalLat += lat;
    }

    const averageLng = totalLng / coordinates.length;
    const averageLat = totalLat / coordinates.length;

    return [averageLng, averageLat];
  };

  changeZoom = (value: number) => {
    const curZoom = this.getMap().getZoom();
    this.getMap().setZoom(curZoom + value);
  };
}

export const stEditorAmap = new STAMap();
